import { UNAVAILABLE_REASONS } from "_shared/_api/by-ids/by-ids-result";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import BookmarkedIndividual from "marketplace/bookmarks/individual-bookmarks/bookmarked-individual";
import BookmarkedIndividualSchema from "marketplace/bookmarks/individual-bookmarks/_schema/bookmarked-individual-schema";
import IndividualSchema from "marketplace/individuals/_schema/individual-schema";
import Individual from "marketplace/individuals/individual";
import { z } from "zod";

export type BookmarkedIndividualListItemJSON = z.infer<typeof BookmarkedIndividualListItemSchema.BookmarkedIndividualListItemJSONZ>;
export type BookmarkedIndividualListItemParams = z.infer<typeof BookmarkedIndividualListItemSchema.BookmarkedIndividualListItemZ>;

/**
 * A row of the list endpoint can only be refused, never missing: the bookmark exists, so the row
 * stays. It is module-private so that both public schemas can read it.
 */
const listItemUnavailableReasonZ = z.enum(UNAVAILABLE_REASONS).extract(["unauthorized"]);

/**
 * One row of `GET marketplace/individual-bookmarks`: the bookmark, and the person at the disclosure
 * the caller may see. When the caller may not see the person, `individual` is null and
 * `unavailableReason` is "unauthorized".
 */
export default class BookmarkedIndividualListItemSchema {
  public static readonly BookmarkedIndividualListItemJSONZ = z.object({
    ...BookmarkedIndividualSchema.BookmarkedIndividualJSONZ.shape,
    individual: IndividualSchema.IndividualJSONZ.nullable(),
    unavailableReason: listItemUnavailableReasonZ.nullable(),
  });
  public static readonly BookmarkedIndividualListItemZ = z.object({
    bookmark: z.instanceof(BookmarkedIndividual),
    individual: z.instanceof(Individual).optional(),
    unavailableReason: listItemUnavailableReasonZ.optional(),
  });

  public static validate(values: unknown): BookmarkedIndividualListItemParams {
    return parseOrThrow(BookmarkedIndividualListItemSchema.BookmarkedIndividualListItemZ, values, {
      logError: true,
      schemaName: "BookmarkedIndividualListItemSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): BookmarkedIndividualListItemJSON {
    return parseOrThrow(BookmarkedIndividualListItemSchema.BookmarkedIndividualListItemJSONZ, values, {
      logError: true,
      schemaName: "BookmarkedIndividualListItemSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is BookmarkedIndividualListItemJSON {
    try {
      BookmarkedIndividualListItemSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }
}
